import type { CurrencyCode } from '@bbpos/types';
import { formatPaise } from '@/utils/currency';
import type { TimelineItem } from '@bbpos/ui';
import type { User, UserShift } from '@/types/user';

/**
 * Build the timeline items shown on the User Detail "Activity" tab.
 *
 * Extracted from UserActivityTab so the data assembly can be unit-tested.
 * Items without a usable timestamp are dropped.
 */
export function buildUserActivityTimelineItems(
  user: User,
  shifts: UserShift[],
  currency: CurrencyCode = 'INR',
): TimelineItem[] {
  const items: TimelineItem[] = [];

  if (user.last_login_at) {
    items.push({
      id: 'last-login',
      timestamp: user.last_login_at,
      action: 'Last login',
      description: 'Most recent session authentication',
      type: 'active',
    });
  }

  for (const shift of shifts) {
    if (shift.clock_out) {
      items.push({
        id: `shift-close-${shift.id}`,
        timestamp: shift.clock_out,
        action: 'Shift closed',
        description: shift.location_name
          ? `${shift.location_name} — ${shift.total_bills ?? 0} bills, ${formatPaise(shift.total_sales ?? 0, currency)}`
          : `${shift.total_bills ?? 0} bills processed`,
        type: 'completed',
      });
    }
    if (shift.clock_in) {
      items.push({
        id: `shift-open-${shift.id}`,
        timestamp: shift.clock_in,
        action: 'Shift opened',
        description: shift.location_name
          ? `${shift.location_name}${shift.shift_name ? ` — ${shift.shift_name}` : ''}`
          : shift.shift_name ?? 'Shift started',
        type: 'inProgress',
      });
    }
  }

  if (user.created_at) {
    items.push({
      id: 'created',
      timestamp: user.created_at,
      action: 'Account created',
      description: `Joined as ${user.role.replace(/_/g, ' ')}`,
      type: 'completed',
    });
  }

  items.sort((a, b) => {
    const at = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const bt = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return bt - at;
  });

  return items;
}
